import { useCallback, useRef, useState } from "react";
import { DeferredLink } from "@/lib/services/linkService/linkService.types";
import { buildFingerprint } from "@/lib/utils/buildFingerprint";
import { rememberEscapeClickId } from "@/lib/inAppEscape/reportEscape";

const CLICK_API_ENDPOINT = "/api/link/click";

type UseClickReporterParams = {
  originalPath: string;
  deferredLink: DeferredLink;
  pasteboardToken: string | null;
  shortLinkUrl?: string;
  isBotRequest: boolean;
};

/**
 * Records the click via the BotID-protected API, fingerprint attached in the
 * same call. Only the first call per page load reaches the network, so every
 * flow below can ask for a click without coordinating with the others.
 */
export function useClickReporter({
  originalPath,
  deferredLink,
  pasteboardToken,
  shortLinkUrl,
  isBotRequest,
}: UseClickReporterParams) {
  const hasCreatedClick = useRef(false);
  const [recordedClickId, setRecordedClickId] = useState<string | null>(null);

  const createClick = useCallback(
    async (appPreviewShown = false): Promise<string | null> => {
      if (hasCreatedClick.current || isBotRequest) return null;
      hasCreatedClick.current = true;

      try {
        const { click_id, ...fingerprint } = await buildFingerprint(
          "",
          originalPath,
          deferredLink.url
        );
        void click_id;

        const response = await fetch(CLICK_API_ENDPOINT, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            link_id: deferredLink.id,
            original_path: originalPath,
            pasteboard_token: pasteboardToken,
            short_link_url: shortLinkUrl ?? null,
            fingerprint,
            app_preview_shown: appPreviewShown,
          }),
        });

        if (!response.ok) return null;
        const data = await response.json();
        const clickId = (data.clickId as string | null) ?? null;
        setRecordedClickId(clickId);

        // So an escape started from a store button can report against this
        // click without every such button having to carry one. See
        // lib/inAppEscape.
        rememberEscapeClickId(clickId);

        return clickId;
      } catch (e) {
        console.error("Click creation failed:", e);
        return null;
      }
    },
    [isBotRequest, originalPath, deferredLink, pasteboardToken, shortLinkUrl]
  );

  return { createClick, clickId: recordedClickId };
}

export type CreateClick = ReturnType<typeof useClickReporter>["createClick"];
